import { BaseEntity, ErrorResponse } from '@lemon/core/model/common'

export enum ArkistoFileType {
  INTERNAL,
  VISIBLE_FOR_CUSTOMER,
}

export enum ArkistoFileTiedostoTyyppi {
  // Tilinpäätösdokumentit — automaation luomia, ei valittavissa manuaalisesti
  TILINPAATOS_ALLEKIRJOITETTU = 'tilinpaatos-allekirjoitettu',
  TILINPAATOS_LUONNOS = 'tilinpaatos-luonnos',
  TILINPAATOS_TILINTARKASTUSKERTOMUS = 'tilintarkastuskertomus',
  TILINPAATOS_TASE_ERITTELY = 'tase-erittely',
  TILINPAATOS_LUETTELO_KIRJANPIDOISTA = 'luettelo-kirjanpidoista',
  TILINPAATOS_PRH_REKISTEROINTI = 'prh-rekisterointi',
  TILINPAATOS_HALLITUKSEN_PAATOS = 'hallituksen-paatos',
  TILINPAATOS_YHTIOKOKOUKSEN_POYTAKIRJA = 'yhtiokokouksen-poytakirja',
  // Käyttäjän valittavissa olevat tyypit
  VEROTUS = 'verotus',
  YHTIOPAPERIT = 'yhtiopaperit',
  LAINAPAPERIT = 'lainapaperit',
  VUOKRASOPIMUKSET = 'vuokrasopimukset',
  KIRJEENVAIHTO = 'kirjeenvaihto',
  TILINTARKASTUS = 'tilintarkastus',
  MUUTA = 'muuta',
  ALKUSALDOT = 'alkusaldot',
}

export type ArkistoKategoria = 'tilinpaatosdokumentit' | 'verotus' | 'yhtiopaperit' | 'lainapaperit' | 'vuokrasopimukset' | 'kirjeenvaihto' | 'tilintarkastus' | 'muuta'

export const ARKISTO_KATEGORIANIMET: Record<ArkistoKategoria, string> = {
  tilinpaatosdokumentit: 'Tilinpäätösdokumentit',
  verotus: 'Verotus',
  yhtiopaperit: 'Yhtiöpaperit',
  lainapaperit: 'Lainapaperit',
  vuokrasopimukset: 'Vuokrasopimukset',
  kirjeenvaihto: 'Kirjeenvaihto',
  tilintarkastus: 'Tilintarkastus',
  muuta: 'Muuta',
}

export interface ArkistoFile extends BaseEntity {
  avain: string
  asiakasAvain: string
  fileName: string
  lisatiedot: string
  type: ArkistoFileType
  /** Tilikausi, johon arkistotiedosto liittyy */
  tilikausiAvain?: string
  /** Tilikaudet, joissa arkistofile saa näkyä */
  tilikaudet?: string[]
  /** Tiedoston tyyppi (käytössä tällä hetkellä automaation luomissa tilinpäätösdokumentteissa) */
  tiedostoTyyppi?: ArkistoFileTiedostoTyyppi
  /** Kieliversiointi (käytössä tällä hetkellä kieliversioiduissa automaation luomissa tilinpäätösdokumentteissa) */
  kieli?: string
  /** Tallennuspaikka (bucket), jossa tiedosto sijaitsee */
  storageBucket?: string
  /** Polku tiedostoon tallennuspaikassa */
  storagePath?: string

  /** Merkintä siitä, että tiedosto on luotu migraation kautta. */
  luotuMigraatiolla?: 'takautuvat-tilinpaatosdokumentit-hallintokansioon'
}

export interface ArkistoDownloadFileRequest {
  asiakasAvain: string
  metadata: ArkistoFile
}

export interface ArkistoDownloadFileResponse extends ErrorResponse<'missing-data' | 'unknown-error'> {
  base64File: string
}

export interface ArkistoGetDownloadLinkRequest {
  asiakasAvain: string
  metadata: ArkistoFile
}

export interface ArkistoGetDownloadLinkResponse extends ErrorResponse<'missing-data' | 'unauthorized' | 'unknown-error'> {
  url: string
}

export interface ArkistoMoveFilesRequest {
  asiakasAvain: string
  tiedostonAvain: string
}

export interface ArkistoMoveFilesResponse extends ErrorResponse<'missing-data' | 'unauthorized' | 'unknown-error'> {}

/** Tiedostotyypit jotka ovat automaation luomia tilinpäätösdokumentteja */
const AUTOMAATION_LUOMAT_TIEDOSTOTYYPIT: ReadonlySet<ArkistoFileTiedostoTyyppi> = new Set([
  ArkistoFileTiedostoTyyppi.TILINPAATOS_ALLEKIRJOITETTU,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_LUONNOS,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_TILINTARKASTUSKERTOMUS,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_TASE_ERITTELY,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_LUETTELO_KIRJANPIDOISTA,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_PRH_REKISTEROINTI,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_HALLITUKSEN_PAATOS,
  ArkistoFileTiedostoTyyppi.TILINPAATOS_YHTIOKOKOUKSEN_POYTAKIRJA,
])

const TIEDOSTOTYYPPI_KATEGORIA: Record<ArkistoFileTiedostoTyyppi, ArkistoKategoria> = {
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_ALLEKIRJOITETTU]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_LUONNOS]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_TILINTARKASTUSKERTOMUS]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_TASE_ERITTELY]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_LUETTELO_KIRJANPIDOISTA]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_PRH_REKISTEROINTI]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_HALLITUKSEN_PAATOS]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.TILINPAATOS_YHTIOKOKOUKSEN_POYTAKIRJA]: 'tilinpaatosdokumentit',
  [ArkistoFileTiedostoTyyppi.VEROTUS]: 'verotus',
  [ArkistoFileTiedostoTyyppi.YHTIOPAPERIT]: 'yhtiopaperit',
  [ArkistoFileTiedostoTyyppi.LAINAPAPERIT]: 'lainapaperit',
  [ArkistoFileTiedostoTyyppi.VUOKRASOPIMUKSET]: 'vuokrasopimukset',
  [ArkistoFileTiedostoTyyppi.KIRJEENVAIHTO]: 'kirjeenvaihto',
  [ArkistoFileTiedostoTyyppi.TILINTARKASTUS]: 'tilintarkastus',
  [ArkistoFileTiedostoTyyppi.MUUTA]: 'muuta',
  [ArkistoFileTiedostoTyyppi.ALKUSALDOT]: 'muuta',
}

export class ArkistoService {
  onAutomaationLuomaTilinpaatosdokumentti(file: Pick<ArkistoFile, 'tiedostoTyyppi'>): boolean {
    return !!file.tiedostoTyyppi && AUTOMAATION_LUOMAT_TIEDOSTOTYYPIT.has(file.tiedostoTyyppi)
  }

  annaKategoria(file: Pick<ArkistoFile, 'tiedostoTyyppi'>): ArkistoKategoria {
    if (!file.tiedostoTyyppi) {
      return 'muuta'
    }
    return TIEDOSTOTYYPPI_KATEGORIA[file.tiedostoTyyppi] ?? 'muuta'
  }

  annaKategorianTiedostotyypit(kategoria: ArkistoKategoria): ArkistoFileTiedostoTyyppi[] {
    return (Object.entries(TIEDOSTOTYYPPI_KATEGORIA) as [ArkistoFileTiedostoTyyppi, ArkistoKategoria][]).filter(([, k]) => k === kategoria).map(([tyyppi]) => tyyppi)
  }

  onTilintarkastajalleSallittuTiedosto(file: Pick<ArkistoFile, 'type'>): boolean {
    return file.type === ArkistoFileType.VISIBLE_FOR_CUSTOMER
  }

  getArkistoPublicFileStorageUri(asiakasAvain: string, file: Pick<ArkistoFile, 'avain'>) {
    if (!file.avain) {
      throw new Error('Arkisto file has no key')
    }
    return asiakasAvain + '/public-files/' + file.avain
  }

  getArkistoInternalFileStorageUri(asiakasAvain: string, file: Pick<ArkistoFile, 'avain'>) {
    if (!file.avain) {
      throw new Error('Arkisto file has no key')
    }
    return asiakasAvain + '/internal-files/' + file.avain
  }

  getArkistoMetadataCollection(asiakasAvain: string) {
    return 'asiakkaat/' + asiakasAvain + '/arkisto/'
  }

  getArkistoMetadataUri(asiakasAvain: string, metadataAvain: string) {
    return this.getArkistoMetadataCollection(asiakasAvain) + metadataAvain
  }
}
